import parseAsNumber from './parseAsNumber';
export const imageBreakpoints = {
    0: { breakpoint: 'base', desiredWidth: 390 },
    390: { breakpoint: 'xs', desiredWidth: 480 },
    480: { breakpoint: 'xs', desiredWidth: 640 },
    640: { breakpoint: 'xs', desiredWidth: 700 },
    700: { breakpoint: 'sm', desiredWidth: 834 },
    834: { breakpoint: 'md', desiredWidth: 960 },
    960: { breakpoint: 'md', desiredWidth: 1080 },
    1080: { breakpoint: 'lg', desiredWidth: 1280 },
    1280: { breakpoint: 'lg', desiredWidth: 1440 },
    1440: { breakpoint: 'xl', desiredWidth: 1600 },
    1600: { breakpoint: 'xl', desiredWidth: 1760 },
    1760: { breakpoint: 'xl', desiredWidth: 1920 },
    1920: { breakpoint: '2xl', desiredWidth: 2200 },
};
// Must be the same as "\src\Dept.Optimizely.Web\appsettings.json ImageResize ImageAllowedWidths"
export const IMAGE_WIDTHS = [390, 480, 640, 700, 834, 1080, 1120, 1280, 1440, 1600, 1760, 1920];
export const DEFAULT_QUALITY = 50;
/**
 * Generate a URL which is compatible with Cloudflare's CDN.
 * @see {@link https://docs.developers.optimizely.com/digital-experience-platform/docs/image-resizing-using-cdn#use-cloudflares-image-resizing-feature-and-the-url-format}
 * @param imageParams The image parameters
 * @returns The CDN compatible URL
 */
export function createCDNImageUrl({ baseUrl, focalPoint, format, quality = DEFAULT_QUALITY, ratio: rawRatio, width: rawWidth, }) {
    const hasWidthAndRatio = typeof rawWidth === 'number' && typeof rawRatio === 'number';
    const ratio = parseAsNumber(rawRatio);
    const width = parseAsNumber(rawWidth);
    const height = hasWidthAndRatio ? Math.floor(width / ratio) : undefined;
    const urlParams = [
        // include default parameters here first
        'fit=cover',
    ];
    if (focalPoint) {
        // Old format was 0.5,0.5 but Cloudflare expects 0.5x0.5
        urlParams.push(`gravity=${focalPoint.replace(',', 'x')}`);
    }
    if (format) {
        urlParams.push(`format=${format}`);
    }
    if (height) {
        urlParams.push(`height=${height}`);
    }
    if (quality) {
        urlParams.push(`quality=${quality}`);
    }
    if (width) {
        urlParams.push(`width=${width}`);
    }
    // /cdn-cgi/image/<OPTIONS>/<SOURCE-IMAGE>
    return `/cdn-cgi/image/${urlParams.join(',')}${baseUrl}`;
}
export function createDefaultImageUrl({ width: rawWidth, quality = DEFAULT_QUALITY, ratio: rawRatio, baseUrl, format, focalPoint, }) {
    const ratio = parseAsNumber(rawRatio);
    const width = parseAsNumber(rawWidth);
    const height = width * ratio;
    const searchParams = new URLSearchParams();
    if (focalPoint) {
        searchParams.append('rxy', focalPoint);
    }
    if (format) {
        searchParams.append('format', format);
    }
    if (height) {
        searchParams.append('height', height.toString());
    }
    if (quality) {
        searchParams.append('quality', quality.toString());
    }
    if (width) {
        searchParams.append('width', width.toString());
    }
    return `${baseUrl}?${searchParams}`;
}
export function createImageUrl({ baseUrl, format, focalPoint, quality = DEFAULT_QUALITY, ratio, useCdn = false, width, }) {
    if (useCdn) {
        return createCDNImageUrl({ width, quality, ratio, baseUrl, format, focalPoint });
    }
    else {
        return createDefaultImageUrl({ width, quality, ratio, baseUrl, format, focalPoint });
    }
}
export function generateSrcSet({ ...params }) {
    const getWidth = (desiredWidth) => {
        const fallback = IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
        return IMAGE_WIDTHS.find((width) => width >= desiredWidth) ?? fallback;
    };
    const breakpointWidths = Object.keys(imageBreakpoints).map((value) => parseAsNumber(value));
    return breakpointWidths
        .reduce((acc, breakpointWidth) => {
        const key = imageBreakpoints[breakpointWidth].breakpoint;
        const desiredWidth = imageBreakpoints[breakpointWidth].desiredWidth;
        const breakpoint = params.sizes?.[key];
        const fallbackRatio = acc.ratio;
        const fallbackVw = acc.vw || 1;
        const fallbackWidth = acc.width;
        const ratio = breakpoint?.ratio ? Number(breakpoint?.ratio) || 1 : fallbackRatio;
        const width = breakpoint?.width || fallbackWidth;
        const vw = breakpoint?.vw ? Number(breakpoint?.vw || 100) : fallbackVw;
        const create = (format) => {
            // If a pixel width was provided always use that instead of a vw
            const chosenWidth = width || getWidth(desiredWidth * vw);
            const src = createImageUrl({ ...params, ratio, format, width: chosenWidth });
            const srcHdpi = createImageUrl({ ...params, ratio, format, width: chosenWidth });
            return {
                id: `${breakpointWidth}-${format ?? 'default'}`,
                media: breakpointWidth === 0 ? undefined : `(min-width: ${breakpointWidth}px)`,
                type: format ? `image/${format}` : undefined,
                src,
                srcHdpi,
            };
        };
        return {
            ratio,
            width,
            vw,
            value: [...acc.value, create(), create('webp')],
        };
    }, { value: [] })
        .value.filter((item, pos, items) => items[pos - 2]?.src !== item.src)
        .reverse();
}
